import React, { useEffect, useRef, useState } from "react";
import CryptoJS from "crypto-js";
import Viewer from "viewerjs";
import config from "../../utils/config";
import "viewerjs/dist/viewer.css";
import "./style.css";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
  useMediaQuery,
  Tooltip,
} from "native-base";
import { Svg } from "react-native-svg";
import { COLORS } from "../../utils/colors";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../utils/screens";
import CustomButton from "../button";
import CustomModal from "../modal";
import MobileModelSheet from "../actionSheet";
import { Toaster } from "../toaster";
import { LoaderAnimation } from "../spinner";
import { deleteImageReviewAPI } from "../../api/apis/workflow";
import { approvedIconSvg } from "../../utils/svg";
import VideoViewer from "../videoViewer";
import { isVideoByExtension } from "../../utils/fileUtils";

// Simple circle SVG for unselected state
const circleSVG = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="#666"
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

  const ImageViewer = ({
  images,
  path = false,
  toolbar = true,
  notification = false,
  showSelectAll = false,
  showTopControls = true,
  showIndividualCheckboxes = true,
  showUploadedBy = false,
  onImagesDeleted = () => {},
  onVideoClick = null,
  externalSelectedImages = [],
  onSelectionChange = null,
}) => {
  const bytes = CryptoJS.AES.decrypt(
    sessionStorage.getItem("content"),
    config.userKey
  );
  const jsonString = bytes.toString(CryptoJS.enc.Utf8);
  const userInfo = JSON.parse(jsonString);
  
  // Check if current user has uploaded any of the images or is admin
  const userHasUploadedImages = images?.some(image => 
    ((image?.uploaded_email === userInfo?.email) || userInfo?.role === 'admin')
  );
  
  // Show selection controls if user has uploaded images OR is admin
  const shouldShowSelection = showSelectAll && (userHasUploadedImages || userInfo?.role === 'admin');
  
  // Show individual checkboxes if user has uploaded images OR is admin, and if showIndividualCheckboxes is true
  const shouldShowCheckboxes = showIndividualCheckboxes && (userHasUploadedImages || userInfo?.role === 'admin');
  const viewerRef = useRef(null);
  const [loaded, setLoaded] = useState(Array(images.length).fill(false));
  const viewerInstance = useRef(null);
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH,
  });
  const toaster = Toaster();

  // Selection state - use external state if provided, otherwise use internal state
  const [internalSelectedImages, setInternalSelectedImages] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const selectedImages = onSelectionChange ? externalSelectedImages : internalSelectedImages;
  const setSelectedImages = onSelectionChange ? onSelectionChange : setInternalSelectedImages;

  useEffect(() => {
    if (viewerRef.current) {
      viewerInstance.current?.destroy();
      viewerInstance.current = new Viewer(viewerRef.current, {
        title: false,
        navbar: false,
        toolbar: toolbar
          ? {
              zoomIn: 1,
              zoomOut: 1,
              reset: 1,
              prev: 1,
              next: 1,
              rotateLeft: 1,
              rotateRight: 1,
              flipHorizontal: 1,
              flipVertical: 1,
            }
          : false,
      });
    }

    return () => viewerInstance.current?.destroy();
  }, [images]);

  const handleImageLoaded = (index) => {
    setLoaded((prevLoaded) => {
      const newLoaded = [...prevLoaded];
      newLoaded[index] = true;
      return newLoaded;
    });
  };

  // Selection handlers
  const handleSelectAll = () => {
    // For admin users, select all images. For regular users, select only their uploaded images
    const imagesToSelect = userInfo?.role === 'admin' 
      ? images 
      : images?.filter(image => image?.uploaded_email === userInfo?.email);
    
    const imageIndices = imagesToSelect?.map((_, index) => {
      const originalIndex = images?.findIndex(img => img === imagesToSelect[index]);
      return originalIndex;
    }).filter(index => index !== -1);
    
    if (selectedImages?.length === imageIndices?.length) {
      // If all relevant images are selected, deselect all
      setSelectedImages([]);
    } else {
      // Select all relevant images
      const allImageIds = imageIndices?.map(index => ({
        index,
        workflow_instance_id: images[index]?.workflow_instance_id || images[index]?._id || index.toString(),
      }));
      setSelectedImages(allImageIds);
    }
  };

  const handleImageSelect = (index, workflowInstanceId) => {
    const imageId = workflowInstanceId || index.toString();
    
    const updateSelection = (prev) => {
      // Ensure prev is always an array
      const prevArray = Array.isArray(prev) ? prev : [];
      const isSelected = prevArray?.some(img => img?.index === index);
      if (isSelected) {
        return prevArray?.filter(img => img?.index !== index);
      } else {
        return [...prevArray, { index, workflow_instance_id: imageId }];
      }
    };
    
    setSelectedImages(updateSelection);
  };

  const handleDeleteSelected = async () => {
    if (selectedImages?.length === 0) return;

    setIsDeleting(true);
    try {
      LoaderAnimation.show();
      
      const workflowInstanceIds = selectedImages?.map(img => img?.workflow_instance_id);
      const payload = { workflow_instance_ids: workflowInstanceIds };
      const response = await deleteImageReviewAPI(payload);

      // Check if response has error property
      if (response && response?.error) {
        toaster(response?.message || 'Failed to delete images', 'error', 'error');
      } else {
        // Success case - use the actual response message
        const successMessage = response?.data?.message || response?.message || 'Images deleted successfully';
        toaster(successMessage, 'success', 'success');
        setSelectedImages([]);
        
        // Call the callback to refresh the images
        if (typeof onImagesDeleted === 'function') {
          onImagesDeleted();
        }
      }
    } catch (error) {
            toaster('An error occurred while deleting images', 'error', 'error');
    } finally {
      setIsDeleting(false);
      LoaderAnimation.hide();
      setShowDeleteModal(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const getDeleteModalHeader = () => {
    return <>Delete Images</>;
  };

  const getDeleteModalBody = () => {
    return (
      <Box justifyContent={'center'} alignItems={'center'}>
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
        >
          Are you sure you want to delete {selectedImages?.length} selected image{selectedImages?.length > 1 ? 's' : ''}?
        </Text>
      </Box>
    );
  };

  const getDeleteModalFooter = () => {
    return (
      <HStack justifyContent={'end'} width={'100%'} space={4}>
        <CustomButton
          onPress={closeDeleteModal}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={handleDeleteSelected}
          text={isDeleting ? 'Deleting...' : 'Delete'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.REJECT_TEXT_COLOR}
          isLoading={isDeleting}
        />
      </HStack>
    );
  };

  const isImageSelected = (index) => {
    return selectedImages?.some(img => img?.index === index);
  };

  // Helper function to render uploaded by card with conditional tooltip
  const renderUploadedByCard = (name) => {
    if (!name) return null;
    
    const displayText = `Uploaded by : ${name}`;
    const shouldShowTooltip = name?.length > 22;
    
    const cardContent = (
      <div className="uploadedByCard">
        {displayText}
      </div>
    );
    
    return shouldShowTooltip ? (
      <Tooltip label={name} placement="bottom" hasArrow>
        {cardContent}
      </Tooltip>
    ) : cardContent;
  };

  // Check if all relevant images are selected
  const relevantImages = userInfo?.role === 'admin' 
    ? images 
    : images?.filter(image => image?.uploaded_email === userInfo?.email);
  const isAllSelected = selectedImages?.length === relevantImages?.length && relevantImages?.length > 0;

  return (
    <VStack space={4}>
      {/* Select All Button */}
      {shouldShowSelection && showTopControls && images?.length > 0 && (
        <HStack justifyContent="end" alignItems="end" 
        style={{
          gap: '10px'
        }}>
          {selectedImages?.length > 0 && (
            <CustomButton
              onPress={() => setShowDeleteModal(true)}
              text={`Delete All (${selectedImages?.length})`}
              color={COLORS.WHITE_COLOR}
              backgroundColor={COLORS.REJECT_TEXT_COLOR}
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              py={2}
              px={4}
            />
          )}

          <CustomButton
            onPress={handleSelectAll}
            text={isAllSelected ? 'Deselect All' : `Select All (${relevantImages?.length})`}
            color={COLORS.WHITE_COLOR}
            backgroundColor={isAllSelected ? COLORS.MODAL_BACK_BUTTON_COLOR : COLORS.THEME_COLOR}
            fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
            py={2}
            px={4}
          />
        </HStack>
      )}

      {/* Images Grid */}
      <div ref={viewerRef} className="imageViewerContainer">
        {images?.map((image, index) => {
          const isSelected = isImageSelected(index);
          const workflowInstanceId = image?.workflow_instance_id || image?._id || index.toString();
          const isVideo = image?.type === 'video' || image?.file_type === 'video' || isVideoByExtension(image?.file_name || image?.path || '');
          
          return (
            <div key={index} className="imageWrapper">
              <div
                className="imageContainer"
                style={{ 
                  background: notification ? "none" : "#f0f0f0",
                  position: "relative"
                }}
              >
              {!loaded[index] && !isVideo && <div className="loadingStyle">Loading...</div>}
              
              {isVideo ? (
                <VideoViewer
                  url={
                    path
                      ? image?.file_name?.[0].includes("http://") ||
                        image?.file_name?.[0].includes("https://") ||
                        image?.file_name?.includes("https://") ||
                        image?.file_name?.includes("http://")
                        ? image?.file_name
                        : config.bucketPath + image?.file_name
                      : image?.file_name?.[0].includes("http://") ||
                        image?.file_name?.[0].includes("https://") ||
                        image?.file_name?.includes("https://") ||
                        image?.file_name?.includes("http://")
                      ? image
                      : config.bucketPath + image
                  }
                  width="100%"
                  height="100%"
                  permission={false}
                  type="any"
                  onVideoClick={onVideoClick}
                />
              ) : (
                <img
                  src={
                    path
                      ? image?.file_name?.[0].includes("http://") ||
                        image?.file_name?.[0].includes("https://") ||
                        image?.file_name?.includes("https://") ||
                        image?.file_name?.includes("http://")
                        ? image?.file_name
                        : config.bucketPath + image?.file_name
                      : image?.file_name?.[0].includes("http://") ||
                        image?.file_name?.[0].includes("https://") ||
                        image?.file_name?.includes("https://") ||
                        image?.file_name?.includes("http://")
                      ? image
                      : config.bucketPath + image
                  }
                  alt=""
                  onLoad={() => handleImageLoaded(index)}
                  style={{
                    display: loaded[index] ? "block" : "none",
                    objectFit: notification ? "initial" : undefined,
                  }}
                />
              )}
              
              {/* Selection Checkbox */}
              {shouldShowCheckboxes && (image?.uploaded_email === userInfo?.email || userInfo?.role === 'admin') && (
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    handleImageSelect(index, workflowInstanceId);
                  }}
                  position="absolute"
                  top={2}
                  right={2}
                  zIndex={100}
                  p={1}
                  bg="rgba(255, 255, 255, 0.95)"
                  borderRadius="full"
                  boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                  style={{ pointerEvents: 'auto' }}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24">
                    {isSelected ? approvedIconSvg : circleSVG}
                  </Svg>
                </Pressable>
              )}
              </div>
              
              {/* Uploaded by card */}
              {showUploadedBy && renderUploadedByCard(image?.uploaded_name)}
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && !MobileWindow ? (
        <CustomModal
          size=''
          top={'20px'}
          isOpen={showDeleteModal}
          onClose={closeDeleteModal}
          header={getDeleteModalHeader()}
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          width='fit-content'
          height='fit-content'
          fixedPosition={true}
          fixedTop={180}
        />
      ) : (
        <MobileModelSheet
          isOpen={showDeleteModal}
          onClose={closeDeleteModal}
          header={getDeleteModalHeader()}
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          maxH={'80vh'}
          position='hidden'
        />
      )}
    </VStack>
  );
};

export default React.memo(ImageViewer);
